import { useEffect, useRef, useState } from 'react';

import { AlertTriangle } from '@gympass/yoga-icons';
import withAuth from 'hocs/withAuth/withAuth';
import { FormattedMessage, useIntl } from 'react-intl';
import { debounce } from 'utils/helpers/lodash';

import { useModals } from 'context';
import useRequest from 'hooks/useRequest';
import Search from 'public/assets/icons/search.svg';
import {
  getPatients,
  Metadata,
  setPatientAttention,
} from 'services/specialist';

import FilterInput from 'components/FilterInput';
import Paginator from 'components/Paginator';
import PatientsTable from 'components/PatientsTable';
import { FeedbackComponentProps } from 'modals/Feedback';

import { Patient } from './types';

import * as S from './styles';

const PatientList: React.FC = () => {
  const intl = useIntl();
  const mountedRef = useRef(true);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filterByAttention, setFilterByAttention] = useState<boolean>(false);
  const [metadata, setMetadata] = useState<Metadata>({} as Metadata);
  const [cursors] = useState<{ [key: string]: string }>({ 1: '' });
  const [pageNumber, setPageNumber] = useState<number>(1);

  const { openModal } = useModals();
  const getPatientListRequest = useRequest();
  const togglePatientAttentionRequest = useRequest();

  const getPatientList = async (
    currentPage: number,
    filterAttention = false,
    patientName = '',
    limit = 10,
  ) => {
    try {
      getPatientListRequest.setLoading();
      const data = await getPatients(
        limit,
        filterAttention,
        cursors[currentPage],
        patientName,
      );

      if (mountedRef.current) {
        setMetadata(data.metadata);

        const users: Patient[] = data.results.map(p => ({
          id: p.id,
          fullName: p.full_name,
          cognitoId: p.cognito_id,
          userId: p.user_id,
          patientAttention: p.patient_attention,
          totalCredits: p.total_credits,
          totalSessions: p.total_session,
          lastSession: p.last_session,
          createdAt: p.created_at,
          notifiesRenewal: p.notifies_renewal,
        }));

        setPatients(users);

        if (data.metadata.nextCursor !== '') {
          cursors[currentPage + 1] = data.metadata.nextCursor;
        }

        getPatientListRequest.setSuccess();
      }
    } catch (e) {
      if (mountedRef.current) {
        const fallbackMessage = 'Error retrieving patients list ';
        getPatientListRequest.setError(e, fallbackMessage);

        openModal<FeedbackComponentProps>('feedback', {
          props: {
            title: getPatientListRequest.errorMessage() || fallbackMessage,
            message: 'ERROR.TRY',
            status: 'error',
          },
        });
      }
    }
  };

  const togglePatientAttention = async (
    cognitoID: string,
    newAttentionValue: boolean,
  ) => {
    try {
      if (mountedRef.current) {
        await setPatientAttention(cognitoID, newAttentionValue);

        const patientsClone = [...patients];
        const patientIdx = patientsClone.findIndex(
          patient => patient.cognitoId === cognitoID,
        );
        const patient = patientsClone[patientIdx];
        patient.patientAttention = newAttentionValue;

        setPatients(patientsClone);
      }
    } catch (e) {
      if (mountedRef.current) {
        const fallbackMessage = 'Error toggle patient attention';

        togglePatientAttentionRequest.setError(e, fallbackMessage);

        openModal<FeedbackComponentProps>('feedback', {
          props: {
            title:
              togglePatientAttentionRequest.errorMessage() || fallbackMessage,
            message: 'ERROR.TRY',
            status: 'error',
          },
        });
      }
    }
  };

  const handleNextPage = () => {
    if (pageNumber === metadata.totalPages) return;
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    getPatientList(nextPage, filterByAttention);
  };

  const handlePreviousPage = () => {
    if (pageNumber === 1) return;
    const previousPage = pageNumber - 1;
    setPageNumber(previousPage);
    getPatientList(previousPage, filterByAttention);
  };

  const handleMoreAttentioFilter = () => {
    setPageNumber(1);
    setFilterByAttention(!filterByAttention);
  };

  const handleSearchPatient = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    await getPatientList(1, filterByAttention, e.target.value);
  };

  const debounceHandleSearchPatient = debounce(handleSearchPatient, 500);

  useEffect(() => {
    getPatientList(1, filterByAttention);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterByAttention]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <S.Container>
      <S.HeaderInfoContainer>
        <S.HeaderLabel data-testid="pageTitle">
          <FormattedMessage id="PATIENT_LIST.PAGE.TITLE" />
        </S.HeaderLabel>
      </S.HeaderInfoContainer>
      <S.Divisor />

      <S.FiltersContainer>
        <S.StyledChips
          data-testid="filterPatientAttentionChips"
          selected={filterByAttention}
          onClick={handleMoreAttentioFilter}
          icons={[null, AlertTriangle]}
        >
          <FormattedMessage id="PATIENT_LIST.PAGE.MORE_ATTENTION" />
        </S.StyledChips>

        <S.SearchInputContainer>
          <FilterInput
            icon={<Search />}
            placeholder={intl.formatMessage({
              id: 'PATIENT_LIST.FILTER.SEARCH',
            })}
            onChange={debounceHandleSearchPatient}
          />
        </S.SearchInputContainer>
      </S.FiltersContainer>

      <S.PatientsTableContainer>
        <PatientsTable
          data={patients}
          onAttentionChange={togglePatientAttention}
          loading={
            togglePatientAttentionRequest.isLoading() ||
            getPatientListRequest.isLoading()
          }
          showNotFoundContent
        />
        {metadata.totalPages > 1 && (
          <S.PaginatorWrapper>
            <Paginator
              startPage={pageNumber}
              lastPage={metadata.totalPages}
              next={handleNextPage}
              previous={handlePreviousPage}
            />
          </S.PaginatorWrapper>
        )}
      </S.PatientsTableContainer>
    </S.Container>
  );
};

export default withAuth(PatientList);
